import { ChannelLogoPicture, ChannelLogoType, StreamCatchupSupport, StreamData } from './../interfaces';
import { ScriptService } from '../controllers/ScriptService';
import { ApiPhysicalChannel, CODEC, Channel, Program, Day, PvrRecording } from '../interfaces';
import i18n from '../i18n';
import { sanitize } from '../i18n/languageMap';
import { hasCatchupEnabled } from './catchupUtils';
import moment from 'moment';
import { isEmpty, upperFirst } from 'lodash';
import { huaweiDateTimeFormat, numberOfLiveCollagePrograms } from '../config';
import { broadcastEnded, broadcastingNow, toMoment } from './huaweiUtils';
import { ChannelConcurrencyGroup } from '../queries/config/types';

export function getMediaId(physicalChannels: ApiPhysicalChannel[]) {
  let mediaId = '';

  physicalChannels.forEach((physicalChannel: ApiPhysicalChannel) => {
    if (ScriptService.supportsHEVC()) {
      if (physicalChannel.videoCodec === CODEC.HEVC) {
        mediaId = physicalChannel.mediaId;
      }
    } else {
      if (physicalChannel.videoCodec === CODEC.AVC) {
        mediaId = physicalChannel.mediaId;
      }
    }
  });

  if (mediaId === '') {
    const physicalChannel = physicalChannels.length > 0 ? physicalChannels[0] : undefined;
    mediaId = physicalChannel && physicalChannel.npvrRecCR ? physicalChannel.npvrRecCR.mi : '';
  }

  return mediaId;
}

export function getChannelPermission(channel: Channel) {
  return channel.channelPermissions;
}

export function getLockedMessage(channel: Channel) {
  const permission = getChannelPermission(channel);
  if (permission.notInSubscription) {
    return i18n.t<string>('permission not in subscription');
  } else if (permission.onlyIptvChannel) {
    return i18n.t<string>('permission only decoder');
  } else if (permission.onlyInsideforLiveTV) {
    return i18n.t<string>('permission only on homenet');
  }
  return '';
}

export function isChannelOnlyIPTV(channel: Channel) {
  const permission = getChannelPermission(channel);
  return permission.onlyIptvChannel;
}

export function createDateLabel(day: number, date: Date) {
  let dateString = '';
  switch (day) {
    case Day.YESTERDAY:
      dateString = i18n.t<string>('yesterday');
      break;
    case Day.TODAY:
      dateString = i18n.t<string>('today');
      break;
    case Day.TOMRROW:
      dateString = i18n.t<string>('tomorrow');
  }
  if (dateString) {
    return upperFirst(dateString + ' ' + createDateString(date, 'D. MMMM'));
  }
  dateString = createDateString(date, 'dddd D. MMMM');
  return upperFirst(dateString);
}

function createDateString(date: Date, format: string) {
  return moment(date).locale(sanitize(i18n.language)).format(format);
}

export function channelHasCatchupEnabled(channel: Channel) {
  if (channel) {
    const cutv = channel.physicalChannel;
    if (cutv) {
      return hasCatchupEnabled(cutv.cutvCR);
    }
  }
  return false;
}

export function formatTimeSpan(starttime: string, endtime: string, format: string = 'HH:mm', isUTC = false): string {
  return [starttime, endtime]
    .map((time) => moment(time))
    .map((time) => time.format(format))
    .join(' - ');
}

export function formatSeasonAndEpisode(seasonNum: string, subNum: string, abbreviation = true): string {
  if (!(seasonNum && subNum)) {
    return '';
  }
  return abbreviation ? `S${seasonNum} E${subNum}` : `${i18n.t('season')} ${seasonNum}, ${i18n.t('episode')} ${subNum}`;
}

export function isCatchup(program: Program): boolean {
  return catchupAvailable(program) && broadcastEnded(program);
}

export function isSupportingCatchup(program: Program): boolean {
  return Boolean(program && program.istvod && program.istvod === StreamCatchupSupport.YES);
}

export function catchupAvailable(program: Program): boolean {
  return program.istvod === '1' && !isEmpty(program.recordedMediaIds);
}

export function isRecording(
  program: Program,
  singleRecordings: PvrRecording[],
  seriesRecordings: PvrRecording[],
): boolean {
  const singleRecording = singleRecordings.find((recording) => recording.programId === program.id);
  if (singleRecording && !broadcastEnded(program)) {
    return true;
  }
  const seriesRecording = seriesRecordings.find((recording) => recording.seriesId === program.seriesID);
  if (seriesRecording && !seriesRecording.canceled) {
    return true;
  }

  return false;
}

export function getChannelLogoUrl({ pictures }: Pick<Channel, 'pictures'>, logoType?: ChannelLogoType): string {
  if (!pictures || isEmpty(pictures)) {
    return '';
  }

  let logo: ChannelLogoPicture | undefined;

  switch (logoType) {
    case ChannelLogoType.CROPPED:
      logo = pictures.find((pic) => pic.imageType === ChannelLogoType.CROPPED);
      break;
    case ChannelLogoType.DEFAULT:
      logo = pictures.find((pic) => pic.imageType === ChannelLogoType.DEFAULT);
      break;
    default:
      logo = pictures.find((pic) => pic.href);
  }

  return logo ? logo.href : '';
}
export function getTimeIntervalHour(hour: number, interval: number) {
  let fromHour = moment().hour(hour).format('HH');
  let toHour = moment()
    .hour(hour + interval)
    .format('HH');
  return `${fromHour}:00-${toHour}:00`;
}

export function programIsBetweenTimeInterval(program: Program, date: Date, selectedHour: number, interval: number) {
  let currentDate = moment(date).date();
  let filterBeginMoment = moment(date, huaweiDateTimeFormat).date(currentDate).hour(selectedHour).minute(0);
  let filterEndMoment = moment(date, huaweiDateTimeFormat)
    .date(currentDate)
    .hour(selectedHour + interval)
    .minute(0);
  return moment(program.starttime, huaweiDateTimeFormat).isBetween(filterBeginMoment, filterEndMoment);
}

export function getSelectedTvGuideFilterList(selectedFilter: string) {
  return selectedFilter.split(',').map((genreString) => genreString.replace(' ', ''));
}

function programContainsGenreId(program: Program, epgGenreList: string[]): Boolean {
  if (isEmpty(program.genreIds)) {
    return false;
  }
  return epgGenreList.some((value) => program.genreIds!.indexOf(value) !== -1);
}

function shouldShowChannel(program: Program, epgGenreList: string[], timePicker: number, date: Date) {
  if (isEmpty(epgGenreList) && timePicker === 0) {
    return true;
  }
  if (!isEmpty(epgGenreList) && timePicker !== 0) {
    return programContainsGenreId(program, epgGenreList) && programIsBetweenTimeInterval(program, date, timePicker, 4);
  } else if (isEmpty(epgGenreList) && timePicker !== 0) {
    return programIsBetweenTimeInterval(program, date, timePicker, 4);
  } else if (!isEmpty(epgGenreList) && timePicker === 0) {
    return programContainsGenreId(program, epgGenreList);
  } else {
    return false;
  }
}

export function filterPlaybills(
  playbills: Program[] | undefined,
  epgGenreList: string[],
  timePicker: number,
  date: Date,
) {
  if (playbills) {
    return playbills.filter((program) => shouldShowChannel(program, epgGenreList, timePicker, date));
  }
  return [];
}

export function getChannelsToShow(channels: Channel[], channelsToUse?: string[]): Channel[] {
  if (!channelsToUse) {
    return [];
  }
  let result = channelsToUse.map((contentId) => {
    return channels.find((a) => a.contentId === contentId);
  });

  if (result.length === numberOfLiveCollagePrograms && !result.includes(undefined)) {
    return result as Channel[];
  }
  return [];
}

export function getOngoingProgramName(programs: Program[]) {
  if (!programs || !Array.isArray(programs) || isEmpty(programs)) {
    return '';
  }

  const program = programs.find(broadcastingNow);

  if (program !== undefined) {
    return program.name;
  } else {
    return '';
  }
}

export function getSortedChannelsBySubscription(channels: Channel[]) {
  if (!channels || isEmpty(channels)) {
    return [];
  }

  return channels.sort((a, b) => {
    if (a.channelPermissions.notInSubscription > b.channelPermissions.notInSubscription) {
      return 1;
    }
    if (a.channelPermissions.notInSubscription < b.channelPermissions.notInSubscription) {
      return -1;
    }
    return 0;
  });
}

const QUERY_PARAM_SERVICE_TYPE = 'servicetype';
const QUERY_PARAM_ZONE_OFFSET = 'zoneoffset';
const QUERY_PARAM_PLAYSEEK = 'playseek';
const QUERY_PARAM_TIMEZONE = 'timezone';
const UTC_TIMEZONE = 'UTC';

/**
 * Takes the manifest url provided by huawei and adds params to utilize scalecast DASH's SegmentTimeline.
 * https://www.unified-streaming.com/blog/stop-numbering-the-underappreciated-power-of-dashs-segmenttimeline
 */
export function addScalecastParams(playData: StreamData, currentProgram?: Program): StreamData {
  let { manifestUrl } = playData;

  // get the start time of the asset (use the current time by default)
  // ATVOTT-9780 fix for caching issues in huawei platform
  const now = moment().utc().subtract(1, 'hour').startOf('hour').format(huaweiDateTimeFormat);

  let startTime: number | string = now;

  if (currentProgram) {
    const playseekTime = toMoment(currentProgram.starttime, false).utc().format(huaweiDateTimeFormat);
    if (playseekTime && playseekTime === 'Invalid date') {
      return playData;
    }
    startTime = playseekTime;
  }

  // the last part of the manifest string separated by | is dash manifest
  let uri = new URL(manifestUrl.split('|')[1]);

  uri.searchParams.delete(QUERY_PARAM_SERVICE_TYPE);
  uri.searchParams.delete(QUERY_PARAM_ZONE_OFFSET);

  // append uri with new parameters
  uri.searchParams.append(QUERY_PARAM_SERVICE_TYPE, '2');
  uri.searchParams.append(QUERY_PARAM_ZONE_OFFSET, '0');

  uri.searchParams.append(QUERY_PARAM_PLAYSEEK, startTime + '-');
  uri.searchParams.append(QUERY_PARAM_TIMEZONE, UTC_TIMEZONE);
  return {
    ...playData,
    manifestUrl: uri.toString(),
  };
}

/**
 * @description
 * - Check if the channel is part of concurrency group and if it is applicable for additional concurrency support
 * - This is due to Huawei not having support for customized concurrency policy
 * - PS: Only applicable for non main channel group!
 */
export function isApplicableForAdditionalConcurrency(
  contentId: string,
  channelConcurrencyGroupsDK?: ChannelConcurrencyGroup[],
) {
  if (!channelConcurrencyGroupsDK || isEmpty(channelConcurrencyGroupsDK)) {
    return false;
  }
  const isMainChannelGroup = channelConcurrencyGroupsDK.some((group) => group.reportTo === contentId);
  if (isMainChannelGroup) {
    return false;
  }

  return channelConcurrencyGroupsDK.flatMap((group) => group.channelIds).some((ch) => ch === contentId);
}
